import { useEffect, useRef } from 'react'

/**
 * Custom hook to handle barcode scanning via keyboard input.
 * It listens for keydown events and accumulates characters into a buffer.
 * When the Enter key is pressed, it triggers the onScan callback with the accumulated input.
 *
 * @param {function} onScan - Callback function to handle scanned input.
 */
type UseBarcodeScanner = (onScan: (output: string) => void) => void

const SCAN_CHARACTER_TIME_THRESHOLD = 50 // Default threshold in milliseconds
const SCAN_MIN_LENGTH = 5 // Minimum length of scanned input to trigger onScan

export const useBarcodeScanner: UseBarcodeScanner = (onScan) => {
  const bufferRef = useRef('')
  let scanTimeout: ReturnType<typeof setTimeout> | undefined

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if the active element is an input or textarea
      // This prevents the scanner input emitted when an input is focused
      const el = document.activeElement
      const inputElFocused = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')
      if (inputElFocused) {
        return
      }

      // Clear any existing timeout, then set a new one
      // that will clear the buffer if the next character is not received within the threshold
      clearTimeout(scanTimeout)
      scanTimeout = setTimeout(() => {
        bufferRef.current = ''
      }, SCAN_CHARACTER_TIME_THRESHOLD)

      // The scanner sends characters one by one, so we accumulate them
      // in the buffer until we receive the Enter key, which indicates the end of the scan
      if (event.key === 'Enter' && bufferRef.current.length >= SCAN_MIN_LENGTH) {
        onScan(bufferRef.current)
        bufferRef.current = ''
      } else {
        bufferRef.current += event.key
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])
}
